



















import Enumerable from "linq";
import { Component, Emit, Mixins, Prop } from "vue-property-decorator";
import { DomainRegistry } from "../../../../domain";
import {
  IDocumentReferable,
  DocumentSummaryHistory,
  DocumentSummary,
} from "../../../../domain/document";
import { MessageBroadcaster } from "../../../system/utilities";
import { AsyncUtility } from "../../../../utilities";
import {
  NavigationPane,
  IButton,
  IButtonState,
} from "../../../panes";
import { DocumentViewerMenuButtons, IDocumentViewerMenuButtonConfiguration, IDocumentViewerMenuButtonsConfiguration, IDocumentViewerMenuConfiguration } from ".";
import { PostPrintingApplicationLauncher } from "../../helpers";

@Component({
  components: {
    NavigationPane,
  },
})
export default class DocumentViewerMenu extends Mixins() {
  // 本来であれば、使う側で拡張できる仕組み(外からコンテンツを差し込めるようにする等)で検討したい所.
  // 今からそう直すのは実際に活用されるユースケースを考えるとコストに見合わないように思われるため、
  // 最大機能(タブとコンテンツ)を実装して、プロパティで必要な機能に絞って利用するよう実装する.
  // あまりいいパターンではないので真似しない事.
  @Prop()
  visibleButtons: any[];

  @Prop()
  isEditNewQuotedDocument: boolean;

  @Prop()
  isMenuEnable: boolean;

  @Prop({ default: true })
  isNoteWritable: boolean;

  @Prop()
  isQuotable: boolean;

  @Prop()
  alternativeFormatOriginalId: number;

  @Prop()
  viewerMenuConfiguration: IDocumentViewerMenuConfiguration;

  get buttons(): IButtonState[] {
    return Enumerable.from([
      {
        definition: this.updateButtonName(c => c.print, DocumentViewerMenuButtons.print),
        isVisible: this.isDocumentPrintable && this.shouldCreateContent(DocumentViewerMenuButtons.print),
        isEnable: true,
        onClick: () => this.print(),
      },
      {
        definition: this.updateButtonName(c => c.printToSelectedPrinter, DocumentViewerMenuButtons.printToSelectedPrinter),
        isVisible: this.isDocumentPrintable && this.shouldCreateContent(DocumentViewerMenuButtons.printToSelectedPrinter),
        isEnable: true,
        onClick: () => this.print(true),
      },
      {
        definition: DocumentViewerMenuButtons.editNewQuotedDocument,
        isVisible: this.shouldCreateContent(DocumentViewerMenuButtons.editNewQuotedDocument) && this.isEditNewQuotedDocumentVisible,
        isEnable: this.isEditNewQuotedDocument && !this.isVirtual,
        onClick: () => this.editNewQuotedDocument(),
      },
      {
        definition: this.updateButtonName(c => c.editNote, DocumentViewerMenuButtons.editNote),
        isVisible: this.isEditNoteVisible,
        isEnable: this.isNoteWritable && !this.isVirtual,
        onClick: () => this.editDocumentNote(),
      },
    ])
    .where(b => b.isVisible)
    .toArray();
  }

  private updateButtonName(accessor: (c: IDocumentViewerMenuButtonsConfiguration) => IDocumentViewerMenuButtonConfiguration, button: IButton): IButton {
    const buttonConfiguration = (this.viewerMenuConfiguration && this.viewerMenuConfiguration.buttons)
      ? accessor(this.viewerMenuConfiguration.buttons)
      : null;
    return {
      name: (buttonConfiguration && buttonConfiguration.name)
        ? buttonConfiguration.name
        : button.name,
      description: (buttonConfiguration && buttonConfiguration.description)
        ? buttonConfiguration.description
        : button.description,
      iconClass: button.iconClass,
    };
  }

  get isDocumentPrintable(): boolean {
    return this.$store.getters.userAuthority.documentPrintable && this.isPrintable;
  }

  get isPrintable(): boolean {
    return !this.document || (this.document && this.document.isPrintable);
  }

  get isEnable(): boolean {
    return this.isMenuEnable === undefined
    ? this.$configuration.behavior.document.viewer.menu.isEnable
    : this.isMenuEnable;
  }

  get isEditNewQuotedDocumentVisible(): boolean {
    return this.isQuotable === undefined 
    ? this.$configuration.behavior.document.viewer.menu.quote.isEnable
    : this.isQuotable;
  }

  get isEditNoteVisible(): boolean {
    return this.$configuration.behavior.document.viewer.menu.note.isEnable;
  }

  get isVirtual(): boolean {
    return (this.document instanceof DocumentSummaryHistory || this.document instanceof DocumentSummary)
      && this.document.isVirtual;
  }

  shouldCreateContent(button: any): boolean {
    return Enumerable.from(this.visibleButtons)
      .any(b => b.name === button.name);
  }

  @Prop()
  document: IDocumentReferable;

  formatId: number;

  get printCopies(): number {
    return this.$configuration.behavior.document.viewer.printing.shouldPrintDefaultCopies
      ? this.document.defaultCopies
      : 1;
  }

  async print(shouldSelectPrinter: boolean = false): Promise<void> {
    this.$logger.info("印刷します。");
    // TODO: hasWatermarkの端末設定（東大では確定登録ではないときに端末設定次第で透かしも印刷させる）
    const launchRequestKey = await this.registerApplicationsLaunchRequest(shouldSelectPrinter);
    this.$printPdf(this.document.printSource.source, this.printCopies, shouldSelectPrinter, false, launchRequestKey);
    await this.finishMajorAction();
  }

  private async registerApplicationsLaunchRequest(withPrinterSelection: boolean): Promise<string> {
    try {
      const launcher = new PostPrintingApplicationLauncher(this.$configuration.behavior.document.postPrinting);
      return await launcher.registerApplicationsLaunchRequest(withPrinterSelection, this.$store.getters.user, this.document.patientNo, this.document.id);
    } catch (e) {
      this.$error("印刷後処理", "印刷後の追加処理要求の登録中にエラーが発生しました。", e);
      return null;
    }
  }

  async finishMajorAction(): Promise<void> {
    if (this.$configuration.behavior.document.viewer.isMajorActionInstant) {
      this.$logger.info("自windowを閉じます.");
      await AsyncUtility.sleep(2000); // urlscheme等の実行前に閉じると困るので2秒待つ.
      this.$closeWindow();
    }
  }

  async editNewQuotedDocument(): Promise<void> {
    this.$logger.info("引用・転記します。");
    if (this.alternativeFormatOriginalId) {
      this.formatId = this.alternativeFormatOriginalId;
    }
    if (!this.formatId) {
      try {
        const repository = DomainRegistry.document.getDocumentRepository();
        const result = await repository.getDocumentSummary(this.document.patientNo, this.document.id);
        this.formatId = result.formatOriginalId;
      } catch (e) {
        this.$fatal(
          "文書概要取得失敗",
          "指定された患者番号(patientNo)と文書ID(documentId)の文書概要取得に失敗しました。",
          e
        );
        return;
      }
    }

    // 別Windowで開く.(完全SPAにするとメモリリーク発生による問題が顕在化しやすくなる恐れがあるため.)
    this.$openWindow(
      "/EditNewQuotedDocument",
      {
        patientNo: this.document.patientNo,
        quotedDocumentId: this.document.id.toString(),
        defaultFormatId: this.formatId.toString(),
      }
    );
    this.finishMajorAction();
  }

  get note(): string {
    return (this.document instanceof DocumentSummaryHistory || this.document instanceof DocumentSummary)
      ? this.document.note
      : "";
  }

  async editDocumentNote(): Promise<void> {
    this.$logger.info(this.$wording.document.note + "を編集します。");
    const note = await this.$commonDialogs.document.noteInput.show(true, this.note, true, this.$wording.document.note);
    if (note !== null && await this.registerDocumentNote(note)) {
      this.notifyDocumentListMessage("documentNoteRegistered");
      this.documentNoteRegistered();
    }
  }

  @Emit()
  documentNoteRegistered(): void {}

  async registerDocumentNote(note: string): Promise<boolean> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      await repository.registerDocumentNote(this.document.id, this.document.patientNo, note);
      this.$success(
        "登録完了",
        this.$wording.document.note + "を登録しました。",
      );
      if (this.document instanceof DocumentSummaryHistory || this.document instanceof DocumentSummary) {
        this.document.note = note;
      }
      return true;
    } catch (e) {
      this.$error(
        "登録失敗",
        this.$wording.document.note + "を登録できませんでした。",
        e,
      );
      return false;
    }
  }

  createPostMessage(key: string, value: any = null): string {
    const message = {
      key,
      value,
    };
    return JSON.stringify(message);
  }

  notifyDocumentListMessage(key: string): void {
    try {
      const message = this.createPostMessage(key);
      if (window.opener) {
        window.opener.postMessage(message, "*");
      } else if (this.$configuration.behavior.localhost.window.opener.isEnable) {
        MessageBroadcaster.sendTo("ShowDocumentList", message);
        MessageBroadcaster.sendTo("ShowPatientDocumentList", message);
      }
    } catch (e) {
      console.log(e);
    }
  }
}
